export const CONTENT_BLOCKER_EVENT = 'rg-content-blocker-signal';
export const CONTENT_BLOCKER_CLEAR_EVENT = 'rg-content-blocker-clear';
export const CONTENT_BLOCKER_DISMISS_KEY = 'rg-content-blocker-notice-dismissed';
export const SCRIPT_BLOCKED_ATTR = 'data-rg-script-blocked';

const probeResults = new Map();
let blockerDetected = false;

export function isContentBlockerDetected() {
  return blockerDetected;
}

export function resetContentBlockerDetectionState() {
  probeResults.clear();
  blockerDetected = false;
}

export function isElementCosmeticallyHidden(element) {
  if (!element || typeof window === 'undefined') {
    return false;
  }

  const style = window.getComputedStyle(element);
  return style.display === 'none' || style.visibility === 'hidden';
}

export function emitContentBlockerSignal(reason) {
  blockerDetected = true;

  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(CONTENT_BLOCKER_EVENT, { detail: { reason } }));
}

/** Hides a false-positive notice if the payment widget starts after a load timeout. */
export function clearContentBlockerSignal() {
  blockerDetected = false;

  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent(CONTENT_BLOCKER_CLEAR_EVENT));
}

function isMarkedBlocked(script) {
  return script?.getAttribute(SCRIPT_BLOCKED_ATTR) === '1';
}

function markScriptBlocked(script) {
  if (script) {
    script.setAttribute(SCRIPT_BLOCKED_ATTR, '1');
  }
}

/**
 * Resolves true only when the script emits error (typical of a content blocker).
 * Timeouts resolve false without caching, so a slow network is not treated as a blocker.
 * An already-failed tag is remembered: remounts cannot listen for a second error event.
 */
export function probeScriptBlocked(src, { timeoutMs = 3000 } = {}) {
  return new Promise((resolve) => {
    if (!src || typeof document === 'undefined') {
      resolve(false);
      return;
    }

    if (probeResults.has(src)) {
      resolve(probeResults.get(src));
      return;
    }

    const existing = document.querySelector(`script[src="${src}"]`);
    if (isMarkedBlocked(existing)) {
      probeResults.set(src, true);
      resolve(true);
      return;
    }

    if (typeof window !== 'undefined' && window.CirenioPayments) {
      probeResults.set(src, false);
      resolve(false);
      return;
    }

    let settled = false;
    const script = existing || document.createElement('script');

    const finish = (blocked, { persist = true } = {}) => {
      if (settled) {
        return;
      }
      settled = true;
      if (persist) {
        probeResults.set(src, Boolean(blocked));
        if (blocked) {
          markScriptBlocked(script);
        }
      }
      resolve(Boolean(blocked));
    };

    script.addEventListener('error', () => finish(true), { once: true });
    script.addEventListener('load', () => finish(false), { once: true });

    if (!existing) {
      script.async = true;
      script.src = src;
      document.body.appendChild(script);
    }

    window.setTimeout(() => finish(false, { persist: false }), timeoutMs);
  });
}

export function isContentBlockerNoticeDismissed() {
  try {
    return sessionStorage.getItem(CONTENT_BLOCKER_DISMISS_KEY) === '1';
  } catch (error) {
    return false;
  }
}

export function dismissContentBlockerNotice() {
  try {
    sessionStorage.setItem(CONTENT_BLOCKER_DISMISS_KEY, '1');
  } catch (error) {
    // Private mode can block sessionStorage; the notice can still be closed in memory.
  }
}
